import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, storePath } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { useStore } from "../../lib/store";
import { uploadFile } from "../../lib/upload";
import { jobDone, useJob } from "../../lib/jobs";
import { Banner, Button, Modal, Spinner } from "../../components/ui";
import type { ListFilter } from "./ProductList";

type Preview = {
  asset_id: string; columns: { header: string; field: string }[]; rows: string[][];
  total_rows: number; products: number; problems: string[];
};
type ImportResult = { created: number; updated: number; skipped: number; failed: number; errors: { row: number; handle: string; message: string }[]; errors_file?: string; notes?: string[] };

const sample = `Handle,Title,Body (HTML),Vendor,Type,Tags,Published,Option1 Name,Option1 Value,Variant SKU,Variant Price,Variant Compare At Price,Image Src,Status
classic-tee,Classic Tee,<p>Soft cotton</p>,Acme,Shirts,"summer, cotton",TRUE,Size,S,TEE-S,49.00,59.00,https://example.com/tee.jpg,active
classic-tee,,,,,,,,M,TEE-M,49.00,59.00,,
`;

export function ImportDialog({ onClose }: { onClose: () => void }) {
  const { t, num } = useI18n();
  const { id } = useStore();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [update, setUpdate] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const job = useJob(id, jobId);
  const result = job.data?.result as ImportResult | undefined;

  const choose = async (file?: File) => {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const asset = await uploadFile(id!, file, "import");
      setPreview(await api<Preview>(storePath(id!, "/product-imports/preview"), { body: { asset_id: asset.id } }));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const start = useMutation({
    mutationFn: () => api<{ job_id: string }>(storePath(id!, "/product-imports"), { body: { asset_id: preview!.asset_id, update_existing: update } }),
    onSuccess: (r) => setJobId(r.job_id),
  });
  const errorsFile = async () => {
    const { url } = await api<{ url: string }>(storePath(id!, `/product-imports/${jobId}/errors`));
    location.assign(url);
  };
  const close = () => {
    if (jobDone(job.data)) qc.invalidateQueries({ queryKey: ["store", id, "products"] });
    onClose();
  };
  const ignored = preview?.columns.filter((c) => !c.field).map((c) => c.header) ?? [];
  const done = jobDone(job.data);

  return (
    <Modal open title={t("import.title")} onClose={close}
      footer={!jobId ? (
        <>
          <Button onClick={close}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={!preview || preview.problems.length > 0} loading={start.isPending} onClick={() => start.mutate()}>{t("import.start")}</Button>
        </>
      ) : <Button variant={done ? "primary" : "secondary"} onClick={close}>{done ? t("product.done") : t("common.cancel")}</Button>}>
      {!jobId && (
        <>
          <p className="text-sm text-stone-600">{t("import.hint")}</p>
          <div className="flex flex-wrap items-center gap-3">
            <input ref={input} type="file" accept=".csv,text/csv" hidden onChange={(e) => choose(e.target.files?.[0])} />
            <Button onClick={() => input.current?.click()} loading={busy}>{t("import.choose")}</Button>
            <a className="text-sm text-blue-700 hover:underline" href={`data:text/csv;charset=utf-8,${encodeURIComponent(sample)}`} download="products-sample.csv">{t("import.sample")}</a>
          </div>
          {(error || start.error) && <Banner tone="critical">{error || start.error?.message}</Banner>}
          {preview && (
            <div className="space-y-3">
              <p className="text-sm font-medium">{t("import.rows", { rows: num(preview.total_rows), products: num(preview.products) })}</p>
              {preview.problems.map((p) => <Banner key={p} tone="critical">{p}</Banner>)}
              {ignored.length > 0 && <p className="text-xs text-stone-500">{t("import.unmapped")}: <span dir="ltr">{ignored.join(", ")}</span></p>}
              <div className="max-h-56 overflow-auto rounded-lg ring-1 ring-stone-200" dir="ltr">
                <table className="text-xs">
                  <thead className="bg-stone-50"><tr>{preview.columns.map((c) => <th key={c.header} className={`whitespace-nowrap px-2 py-1 text-start ${c.field ? "" : "text-stone-400"}`}>{c.header}</th>)}</tr></thead>
                  <tbody>{preview.rows.map((r, i) => <tr key={i} className="border-t border-stone-100">{preview.columns.map((_, j) => <td key={j} className="max-w-48 truncate whitespace-nowrap px-2 py-1">{r[j]}</td>)}</tr>)}</tbody>
                </table>
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={update} onChange={(e) => setUpdate(e.target.checked)} />{t("import.update_existing")}</label>
            </div>
          )}
        </>
      )}
      {jobId && !done && (
        <div className="flex items-center gap-3 text-sm"><Spinner className="size-4" />{t("import.running", { done: job.data?.progress ?? 0, total: job.data?.total ?? "…" })}</div>
      )}
      {done && job.data?.status === "failed" && <Banner tone="critical">{t("job.failed", { error: job.data.error ?? "" })}</Banner>}
      {done && result && (
        <div className="space-y-3">
          <Banner tone={result.failed ? "warning" : "success"}>{t("import.done")}: {t("import.summary", { created: result.created, updated: result.updated, skipped: result.skipped, failed: result.failed })}</Banner>
          {result.notes?.map((n) => <p key={n} className="text-sm text-stone-600">{n}</p>)}
          {result.errors.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">{t("import.errors")}</p>
              <ul className="max-h-48 space-y-1 overflow-auto text-sm">
                {result.errors.map((e, i) => <li key={i}><span className="text-stone-500" dir="ltr">#{e.row} {e.handle}</span> — {e.message}</li>)}
              </ul>
              {result.errors_file && <Button onClick={errorsFile}>{t("import.download_errors")}</Button>}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

export function ExportDialog({ filter, selected, onClose }: { filter: ListFilter | null; selected: string[]; onClose: () => void }) {
  const { t } = useI18n();
  const { id } = useStore();
  const [scope, setScope] = useState<"all" | "filtered" | "selected">(selected.length ? "selected" : filter ? "filtered" : "all");
  const [excel, setExcel] = useState(true);
  const [jobId, setJobId] = useState<string | null>(null);
  const job = useJob(id, jobId);
  const start = useMutation({
    mutationFn: () => api<{ job_id: string }>(storePath(id!, "/product-exports"), {
      body: {
        excel,
        ids: scope === "selected" ? selected : undefined,
        filter: scope === "filtered" && filter ? { status: filter.status ? [filter.status] : undefined, vendor: filter.vendor || undefined,
          product_type: filter.product_type || undefined, tag: filter.tag || undefined, q: filter.q || undefined } : undefined,
      },
    }),
    onSuccess: (r) => setJobId(r.job_id),
  });
  const download = async () => {
    const { url } = await api<{ url: string }>(storePath(id!, `/product-exports/${jobId}/download`));
    location.assign(url);
  };
  const done = jobDone(job.data);
  return (
    <Modal open title={t("export.title")} onClose={onClose}
      footer={!jobId ? (
        <><Button onClick={onClose}>{t("common.cancel")}</Button><Button variant="primary" loading={start.isPending} onClick={() => start.mutate()}>{t("export.start")}</Button></>
      ) : done && job.data?.status === "succeeded" ? <Button variant="primary" onClick={download}>{t("export.download")}</Button> : <Button onClick={onClose}>{t("common.cancel")}</Button>}>
      {!jobId ? (
        <>
          <fieldset className="space-y-2 text-sm">
            <legend className="mb-1 font-medium">{t("export.scope")}</legend>
            <label className="flex items-center gap-2"><input type="radio" checked={scope === "all"} onChange={() => setScope("all")} />{t("export.all")}</label>
            {filter && <label className="flex items-center gap-2"><input type="radio" checked={scope === "filtered"} onChange={() => setScope("filtered")} />{t("export.filtered")}</label>}
            {selected.length > 0 && <label className="flex items-center gap-2"><input type="radio" checked={scope === "selected"} onChange={() => setScope("selected")} />{t("export.selected", { n: selected.length })}</label>}
          </fieldset>
          <fieldset className="space-y-2 text-sm">
            <label className="flex items-center gap-2"><input type="radio" checked={excel} onChange={() => setExcel(true)} />{t("export.excel")}</label>
            <label className="flex items-center gap-2"><input type="radio" checked={!excel} onChange={() => setExcel(false)} />{t("export.plain")}</label>
          </fieldset>
          {start.error && <Banner tone="critical">{start.error.message}</Banner>}
        </>
      ) : !done ? (
        <div className="flex items-center gap-3 text-sm"><Spinner className="size-4" />{t("export.running", { done: job.data?.progress ?? 0, total: job.data?.total ?? "…" })}</div>
      ) : job.data?.status === "succeeded" ? <Banner tone="success">{t("export.ready")}</Banner> : <Banner tone="critical">{t("job.failed", { error: job.data?.error ?? "" })}</Banner>}
    </Modal>
  );
}
